import { IFormInput } from '@components/pdf/form-pdf';
import CryptoJS, { SHA512 } from 'crypto-js';
import moment from 'moment';
import { DayOfWeek } from '../modules/front-office/components/room-management/dayschip';
import { TableRow } from '@components/pdf/react-pdf/react-pdf.interface';

/**
 * The default date format used throughout the application.
 */
export const dateFormat = 'MMM DD, YYYY';
export const dateTimeFormat = 'MMM DD, YYYY h:mm A';
export const payloadDateFormat = 'YYYY-MM-DD';

/**
 * List of allowed file types for uploads.
 */
export const uploadFileTypes = ['JPG', 'PNG', 'SVG'];

/**
 * Formats a number with specified decimal places.
 * @param number - The number to format.
 * @param maxDecimal - The maximum number of decimal places (optional).
 * @param minDecimal - The minimum number of decimal places (optional).
 * @returns A formatted string representation of the number.
 */
export const formatNumber = (
  number: number | string,
  maxDecimal?: number,
  minDecimal?: number,
  returnDashForZero?: boolean,
  useParenthesesForNegative?: boolean,
  config?: { spaceAfterComma?: boolean },
) => {
  const { spaceAfterComma = false } = config ?? {};
  const parsed = typeof number === 'string' ? Number(number) : number;

  if (isNaN(parsed)) return '-';
  if (returnDashForZero && parsed === 0) return '-';

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: minDecimal,
    maximumFractionDigits: maxDecimal,
  });

  let formatted = formatter.format(Math.abs(parsed));

  if (spaceAfterComma) {
    formatted = formatted.replace(/,/g, ', ');
  }

  if (useParenthesesForNegative && parsed < 0) {
    return `(${formatted})`;
  }

  return parsed < 0 ? `-${formatted}` : formatted;
};

/**
 * Truncates a number to two decimal places without rounding.
 * @param {number} num - The number to truncate.
 * @returns {number} The truncated number with exactly two decimal places.
 */
export const truncateNumber = (num: number): number => {
  return Math.trunc(num * 100) / 100; // Truncate without rounding
};

/**
 * Round a number to two decimal places.
 * @param {number} num - The number to round.
 * @returns {number} The number with exactly two decimal places.
 */
export const roundNumber = (num: number): number => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

/**
 * Converts an array of days to a boolean object representation.
 * @param days - An array of DayOfWeek values.
 * @returns An object with boolean values for each day of the week.
 */
export const convertDaysToBooleanObject = (days: DayOfWeek[] | undefined) => {
  const daysObject = {
    sun: false,
    mon: false,
    tues: false,
    wed: false,
    thur: false,
    fri: false,
    sat: false,
  };

  if (days) {
    days.forEach((day) => {
      switch (day) {
        case 'Sun':
          daysObject.sun = true;
          break;
        case 'Mon':
          daysObject.mon = true;
          break;
        case 'Tue':
          daysObject.tues = true;
          break;
        case 'Wed':
          daysObject.wed = true;
          break;
        case 'Thu':
          daysObject.thur = true;
          break;
        case 'Fri':
          daysObject.fri = true;
          break;
        case 'Sat':
          daysObject.sat = true;
          break;
      }
    });
  }

  return daysObject;
};

/**
 * Checks if a given string is a valid UUID.
 * @param uuid - The string to check.
 * @returns True if the string is a valid UUID, false otherwise.
 */
export const isValidUUID = (uuid: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

export const isValidDate = (date: string) =>
  moment(date, payloadDateFormat, true).isValid();

/**
 * Regex for all sorts of testing
 */
export const regex = {
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$|^$/,
  phoneNumber: /^\+?[1-9]\d{1,14}$/,
  phoneNumberNotRequired: /^\+?[1-9]\d{1,14}$|^$/,
  contactNumber: /^(?:\+63|0)9\d{9}$/,
  contactNumberNotRequired: /^(?:(?:\+63|0)9\d{9})?$/,
  website:
    /^(https?:\/\/)?(([\w-]+\.)+)?([a-zA-Z0-9_-]+)(\.[a-zA-Z]{2,})?(:\d+)?(\/\S*)?$/,
  // /^(https?:\/\/)?(www\.)?([a-zA-Z0-9_-]+)(\.[a-zA-Z]{2,})(:\d+)?(\/\S*)?$/,
  websiteNotRequired:
    /^(https?:\/\/)?(([\w-]+\.)+)?([a-zA-Z0-9_-]+)(\.[a-zA-Z]{2,})?(:\d+)?(\/\S*)?$|^$/,
  // /^(https?:\/\/)?(www\.)?([a-zA-Z0-9_-]+)?(\.[a-zA-Z]{2,})?(:\d+)?(\/\S*)?$|^$/,
  tin: /^\d{3}-\d{3}-\d{3}-\d{5}$/,
  vat: /^\d{3}-\d{3}-\d{3}-\d{5}$/,
  // /^\d{3}-\d{3}-\d{3}-000$/,
  tinNotRequired: /^(\d{3}-\d{3}-\d{3}-\d{3})?$/,
  vatNotRequired: /^(\d{3}-\d{3}-\d{3}-\d{5})?$/,
  // /^\d{3}-\d{3}-\d{3}-000$|^$/,
  lettersAndNumber: /[^a-zA-Z0-9]/g,
};

/**
 * Formats a currency amount based on the specified format type.
 * @param amount - The amount to format.
 * @param formatType - The type of formatting to apply.
 * @param currencySymbol - The currency symbol to use (default: '$').
 * @returns A formatted string representation of the currency amount.
 */
export const formatCurrency = (
  amount: number,
  formatType: string,
  currencySymbol: string = '$',
): string => {
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  switch (formatType) {
    case 'option1': // $123,456.00
      return new Intl.NumberFormat('en-US', {
        ...options,
        currencyDisplay: 'symbol',
      })
        .format(amount)
        .replace('$', currencySymbol);
    case 'option2': // 123.456,00 $
      return new Intl.NumberFormat('de-DE', {
        ...options,
        currencyDisplay: 'symbol',
      })
        .format(amount)
        .replace('€', currencySymbol);
    case 'option3': // 123 456,00 $
      return new Intl.NumberFormat('fr-FR', {
        ...options,
        currencyDisplay: 'symbol',
        useGrouping: true,
      })
        .format(amount)
        .replace(/\u00A0/g, ' ')
        .replace('€', currencySymbol);
    case 'option4': // $1,23,456.00
      return new Intl.NumberFormat('en-IN', {
        ...options,
        currencyDisplay: 'symbol',
      })
        .format(amount)
        .replace('$', currencySymbol);
    case 'option5': // $ 123.456,00
      return new Intl.NumberFormat('de-DE', {
        ...options,
        currencyDisplay: 'symbol',
      })
        .format(amount)
        .replace(/^(\D+)/, `${currencySymbol} `);
    default:
      throw new Error('Invalid format type');
  }
};

/**
 * Converts all values in an object to strings.
 * @param obj - The object to convert.
 * @returns A new object with all values converted to strings.
 */
export const convertValuesToString = (obj: any): { [key: string]: string } => {
  const stringifiedObject: { [key: string]: string } = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      stringifiedObject[key] = convertToString(value);
    }
  }

  return stringifiedObject;
};

/**
 * Converts a value to a string representation.
 * @param value - The value to convert.
 * @returns A string representation of the value.
 */
export const convertToString = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return JSON.stringify(value);
  if (value instanceof Date)
    return moment(value).format('MM/DD/YYYY, hh:mm:ss A');
  return String(value);
};

/**
 * Sorts the keys of an object alphabetically.
 * @param obj - The object to sort.
 * @returns A new object with keys sorted alphabetically.
 */
export const sortObjectKeysAlphabetically = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => sortObjectKeysAlphabetically(item));
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj)
      .sort()
      .reduce((sortedObj: { [key: string]: any }, key: string) => {
        sortedObj[key] = sortObjectKeysAlphabetically(obj[key]);
        return sortedObj;
      }, {});
  } else {
    return obj;
  }
};

/**
 * Checks if the given data is a string.
 * @param data - The data to check.
 * @returns True if the data is a string, false otherwise.
 */
export const isDataString = (data: unknown) => {
  try {
    return typeof data === 'string';
  } catch (e) {
    return false;
  }
};

export const generateUUID = () => {
  const hex = CryptoJS.lib.WordArray.random(16).toString();
  return `${hex.substr(0, 8)}-${hex.substr(8, 4)}-4${hex.substr(12, 3)}-${((parseInt(hex.substr(16, 2), 16) & 0x3f) | 0x80).toString(16)}${hex.substr(18, 2)}-${hex.substr(20, 12)}`;
};

/**
 * Flatten object and create a sorted string based on values.
 * Convert boolean values to 1 or 0 and convert undefined/null to empty string.
 * @param obj - The object to flatten.
 * @returns A flattened string representation of the object.
 */
const flattenObjectForSignature = (obj: any): string => {
  const flatObject: { [key: string]: any } = {};

  const flatten = (data: any, prefix = '') => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key];
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value)
        ) {
          flatten(value, newKey);
        } else {
          // Apply specific transformations based on the value type
          flatObject[newKey] =
            value === true
              ? '1'
              : value === false
                ? '0'
                : value === undefined || value === null
                  ? ''
                  : value;
        }
      }
    }
  };

  flatten(obj);

  // Sort keys alphabetically and concatenate their values
  return Object.keys(flatObject)
    .sort()
    .map((key) => String(flatObject[key]))
    .join('');
};

/**
 * Generates ordinal for number values.
 * @param num - The number to generate the ordinal for.
 * @returns Number with ordinal.
 */

export const nth = (n: number) => {
  return n + ['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th';
};

/**
 * Recursively generate a signature for each object containing a "signature" field.
 * Ensures that propertyID is included in the signature calculation.
 * @param data - The data object to generate signatures for.
 * @param context - The context object containing propertyID.
 * @returns A new object with generated signatures.
 */
export const generateSignatures = (
  data: any,
  context: { propertyID?: string } = {},
): any => {
  if (Array.isArray(data)) {
    return data.map((item) => generateSignatures(item, context));
  } else if (typeof data === 'object' && data !== null) {
    const newObj: any = {};

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (key === 'signature') {
          // Generate signature for the object (excluding the signature field itself)
          const objectForSignature = { ...data };
          delete objectForSignature.signature;

          // Ensure propertyID is included in the signature generation
          if (context.propertyID) {
            objectForSignature.propertyID = context.propertyID;
          }

          const signatureString = flattenObjectForSignature(objectForSignature);
          console.log('generated signature: ', signatureString);
          newObj.signature = SHA512(signatureString).toString(); // Generate the hash
        } else if (key === 'propertyID') {
          // Capture the propertyID in the context for deeper recursion
          context.propertyID = data[key];
          newObj[key] = data[key];
        } else if (typeof data[key] === 'object') {
          // Recursively handle nested objects/arrays and pass context
          newObj[key] = generateSignatures(data[key], context);
        } else {
          newObj[key] = data[key];
        }
      }
    }

    return newObj;
  }

  return data;
};

export const isEmptyObj = (obj) => Object.keys(obj).length === 0;

export const formatString = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Handle camelCase
    .replace(/_/g, ' ') // Handle snake_case
    .toLowerCase() // Convert to lowercase
    .split(' ') // Split into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter of each word
    .join(' '); // Join words back together
};

export const formatDateRange = (
  arrivalDate: string,
  departureDate: string,
): string => {
  const arrival = moment(arrivalDate);
  const departure = moment(departureDate);

  if (!arrival.isValid() || !departure.isValid()) return 'Invalid date';

  const arrivalMonth = arrival.format('MMM');
  const departureMonth = departure.format('MMM');

  const arrivalDay = arrival.format('D');
  const departureDay = departure.format('D');

  const arrivalYear = arrival.format('YYYY');
  const departureYear = departure.format('YYYY');

  if (arrivalYear !== departureYear) {
    // Different years: Show full format
    return `${arrivalMonth}. ${arrivalDay}, ${arrivalYear} - ${departureMonth}. ${departureDay}, ${departureYear}`;
  } else if (arrivalMonth !== departureMonth) {
    // Different months: Show month and day for both
    return `${arrivalMonth}. ${arrivalDay} - ${departureMonth}. ${departureDay}, ${arrivalYear}`;
  } else {
    // Same month: Show day range only
    return `${arrivalMonth}. ${arrivalDay}-${departureDay}, ${arrivalYear}`;
  }
};

/**
 * Checks if an object contains a specific field.
 * @param {Object} obj - The object to check.
 * @param {string} field - The field to check for.
 * @returns {boolean} True if the object contains the specified field, otherwise false.
 */
export const containsField = (
  obj: Record<string, any>,
  field: string,
): boolean => {
  return field in obj;
};

export const obscureEmail = (email?: string): string => {
  if (typeof email !== 'string' || !email.includes('@')) {
    return 'Registered Email Address';
  }

  const [name, domain] = email.split('@');

  if (!name || !domain) {
    return 'Registered Email Address';
  }

  const visibleChars = 3;
  const asteriskCount = Math.max(name.length - visibleChars, 0);
  const obscured = name.substring(0, visibleChars) + '*'.repeat(asteriskCount);

  return `${obscured}@${domain}`;
};

export type Stringified<T> = {
  [K in keyof T]: T[K] extends (infer U)[]
    ? U extends object
      ? Stringified<U>[]
      : string[]
    : T[K] extends object
      ? Stringified<T[K]>
      : string;
};

export const pdfValuesToString = <T extends object>(obj: T): Stringified<T> => {
  const stringifiedObject: any = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (Array.isArray(value)) {
        stringifiedObject[key] = value.map((item) =>
          typeof item === 'object' && item !== null
            ? pdfValuesToString(item)
            : convertToString(item),
        );
      } else if (value === null || value === undefined) {
        stringifiedObject[key] = '';
      } else if (typeof value === 'object' && value !== null) {
        stringifiedObject[key] = pdfValuesToString(value);
      } else {
        stringifiedObject[key] = convertToString(value);
      }
    }
  }

  return stringifiedObject;
};

export const toReportInputs = (
  obj: Record<string, any>,
  isEmptyString?: boolean,
): IFormInput => {
  const result: IFormInput = {};

  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

    const value = obj[key];

    if (typeof value === 'string') {
      result[key] = isEmptyString ? '‎ ' : value;
    } else if (typeof value === 'number') {
      result[key] = isEmptyString ? '‎ ' : `${value}`;
    } else if (
      Array.isArray(value) &&
      value.every((row) => typeof row === 'object' && !Array.isArray(row))
    ) {
      const rows = value as Record<string, string>[];

      // Extract headers from the first object
      const headers = Object.keys(rows[0] || {});

      const tableRows: TableRow[] = [];

      // Optional: include header row
      tableRows.push({
        isHeader: true,
        cells: headers.map((h) => ({ value: h })),
      });

      // Add data rows
      for (const row of rows) {
        tableRows.push({
          cells: headers.map((key) => ({
            value: row[key] ?? '',
          })),
        });
      }

      result[key] = tableRows;
    } else {
      result[key] = isEmptyString ? '‎ ' : '';
    }
  }

  return result;
};

export const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export const transformDatesInObject = <T>(
  data: T,
  dateFormat = 'YYYY-MM-DD',
): T => {
  const transform = (value: any): any =>
    Array.isArray(value)
      ? value.map(transform)
      : value && typeof value === 'object'
        ? Object.fromEntries(
            Object.entries(value).map(([key, val]) => {
              const isDateKey = key.toLowerCase().includes('date');

              if (isDateKey) {
                if (typeof val === 'string' || typeof val === 'number') {
                  const m = moment(val);
                  return [key, m.isValid() ? m.format(dateFormat) : val];
                }

                // If value is an array of strings/numbers/objects
                if (Array.isArray(val)) {
                  return [
                    key,
                    val.map((item) => {
                      if (
                        typeof item === 'string' ||
                        typeof item === 'number'
                      ) {
                        const m = moment(item);
                        return m.isValid() ? m.format(dateFormat) : item;
                      } else if (typeof item === 'object') {
                        return transform(item);
                      }
                      return item;
                    }),
                  ];
                }

                // If value is an object under a "date"-key
                if (typeof val === 'object') {
                  return [key, transform(val)];
                }
              }

              return [key, transform(val)];
            }),
          )
        : value;

  return transform(data);
};

export const decimalToRatio = (decimal: number, maxDenominator = 1000) => {
  let bestNumerator = 1;
  let bestDenominator = 1;
  let bestError = Number.MAX_VALUE;

  for (let denominator = 1; denominator <= maxDenominator; denominator++) {
    const numerator = Math.round(decimal * denominator);
    const error = Math.abs(decimal - numerator / denominator);

    if (error < bestError) {
      bestError = error;
      bestNumerator = numerator;
      bestDenominator = denominator;
    }

    if (bestError === 0) break; // exact match
  }

  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(bestNumerator, bestDenominator);

  return `${bestNumerator / divisor}:${bestDenominator / divisor}`;
};

/**
 * Removes leading whitespace from a string.
 *
 * @param value - Raw input value
 * @returns Sanitized string without leading spaces
 */
export const trimLeadingWhiteSpace = (value: string): string =>
  value?.replace(/^\s+/, '');

/**
 * Capitalizes each word.
 * Keeps words that are already fully uppercase (e.g., acronyms).
 */
export const capitalizeWords = (value?: string): string => {
  if (!value) return '';

  return value
    .trim()
    .split(/\s+/)
    .map((word) => {
      // Keep acronyms (all uppercase words)
      if (word === word.toUpperCase()) {
        return word;
      }

      // Capitalize normal words
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

export const parseNumber = (raw: string) =>
  parseFloat(raw?.replace(/,/g, '') ?? '');

export const parseGenparNumber = (
  value: string | undefined,
  fallback: number,
) => {
  const parsed = parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const parseToTwoDecimals = (val: number | string): number => {
  const num = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
};

export const formatTitle = (text: string) => {
  if (!text) return '';

  const words = text
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .split(' ');

  const formatted = words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  return formatted;
};

export const normalizeIpAddress = (ip: string): string =>
  ip
    .split('.')
    .map((segment) => String(Number(segment)))
    .join('.');

export const formatIpAddress = (ip: string): string =>
  ip
    .split('.')
    .map((segment) => segment.padStart(3, '0'))
    .join('.');

export const isValidIPv4 = (ip: string): boolean => {
  const parts = ip.split('.');

  if (parts.length !== 4) return false;

  return parts.every((part) => {
    if (!/^\d+$/.test(part)) return false;

    const num = Number(part);

    return num >= 0 && num <= 255;
  });
};

export const isValidPrinterIPAddress = (ip: string): boolean => {
  const parts = ip.split('.');

  if (parts.length !== 4) return false;

  return parts.every((part) => {
    if (!/^\d{1,3}$/.test(part)) return false;

    const num = Number(part);

    return num >= 0 && num <= 999;
  });
};

/**
 * Formats a raw string of digits into PH-style phone format: 0917-123-4567
 * Strips non-digits and caps at 11 digits.
 */
export const formatPhoneNumber = (value: string): string => {
  const numbers = value.replace(/\D/g, '').slice(0, 11);

  if (numbers.length <= 4) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;

  return `${numbers.slice(0, 4)}-${numbers.slice(4, 7)}-${numbers.slice(7)}`;
};
